/**
 * Token Balance Display Component
 *
 * Shows the user's current token balance with a button to buy more tokens.
 * Clicking on the balance shows a breakdown of expirable vs permanent tokens.
 */

import { memo, useState, useRef } from 'react';
import {
  Plus,
  Clock,
  Infinity as InfinityIcon,
  AlertTriangle,
  Receipt,
  Download,
} from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { isServerlessMode, isElectronMode } from '../../utils/apiMode';
import { publicAsset } from '../../utils/assetUrl';
import sharedConstants from '../../constants/shared_constants.json';
import { ReferralShare } from '../referral';
import { useClickOutside } from '../../hooks/useClickOutside';
import type { TokenBalanceInfo } from '../../hooks/useTokenBalance';

interface TokenBalanceProps {
  /** null when the balance could not be read -- shown as unknown, never as 0. */
  balance: number | null;
  fullBalance?: TokenBalanceInfo | null;
  isLoading?: boolean;
  onBuyClick: () => void;
}

/**
 * Format token balance for display.
 * Uses compact notation for large numbers.
 */
function formatBalance(balance: number): string {
  if (balance >= 1_000_000) {
    return `${(balance / 1_000_000).toFixed(1)}M`;
  }
  if (balance >= 1_000) {
    return `${(balance / 1_000).toFixed(1)}K`;
  }
  return balance.toLocaleString();
}

/**
 * Format date for expiration display.
 */
function formatExpirationDate(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Calculate days until expiration.
 */
function getDaysUntilExpiration(isoDate: string): number {
  const expirationDate = new Date(isoDate);
  const now = new Date();
  return Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

export const TokenBalance = memo(function TokenBalance({
  balance,
  fullBalance,
  isLoading = false,
  onBuyClick,
}: TokenBalanceProps) {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  // Close tooltip when clicking outside
  useClickOutside([tooltipRef, buttonRef], () => setShowTooltip(false), { enabled: showTooltip });

  // No breakdown means the balance could not be read. It used to be filled in
  // with zeros here, which is how an unreachable server showed a funded
  // account as "Total 0 / Expirable 0 / Permanent 0".
  const balanceInfo: TokenBalanceInfo | null = fullBalance ?? null;

  // Calculate expiration warning
  const hasExpiringTokens = Boolean(
    balanceInfo?.next_expiration && balanceInfo.expiring_soon_amount > 0
  );
  const daysUntilExpiration =
    hasExpiringTokens && balanceInfo ? getDaysUntilExpiration(balanceInfo.next_expiration!) : null;
  const showExpirationWarning = daysUntilExpiration !== null && daysUntilExpiration <= 7;

  return (
    <div className="relative flex items-center gap-2">
      {/* Balance display - clickable for breakdown */}
      <div
        ref={buttonRef}
        onClick={() => setShowTooltip(!showTooltip)}
        onMouseEnter={() => setShowTooltip(true)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${
          showExpirationWarning
            ? 'bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50'
            : 'bg-secondary-100 dark:bg-secondary-800 hover:bg-secondary-200 dark:hover:bg-secondary-700'
        }`}
      >
        <img src={publicAsset('token-coin-32.png')} alt="" className="h-5 w-5" draggable={false} />
        <span
          className={`text-sm font-medium ${
            showExpirationWarning
              ? 'text-amber-700 dark:text-amber-300'
              : 'text-secondary-700 dark:text-secondary-200'
          }`}
        >
          {isLoading ? '...' : balance === null ? '—' : formatBalance(balance)}
        </span>
        {showExpirationWarning && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
      </div>

      {/* Tooltip/Popover with balance breakdown */}
      {showTooltip && (
        <div
          ref={tooltipRef}
          onMouseLeave={() => setShowTooltip(false)}
          className="absolute top-full left-0 mt-2 z-50 min-w-[240px]
                     bg-white dark:bg-secondary-800 rounded-lg shadow-lg
                     border border-secondary-200 dark:border-secondary-700
                     animate-in fade-in-0 zoom-in-95 duration-150"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-secondary-200 dark:border-secondary-700">
            <h4 className="text-sm font-semibold text-secondary-900 dark:text-white">
              {t('tokens.balanceBreakdown')}
            </h4>
          </div>

          {/* Content */}
          {balanceInfo === null ? (
            <div className="px-4 py-3">
              <p className="text-sm font-medium text-secondary-900 dark:text-white">
                {t('tokens.balanceUnavailable')}
              </p>
              <p className="mt-1 text-xs text-secondary-600 dark:text-secondary-400">
                {t('tokens.serviceUnreachable')}
              </p>
            </div>
          ) : (
            <div className="px-4 py-3 space-y-3">
              {/* Total Balance */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 dark:text-secondary-400">
                  {t('tokens.total')}
                </span>
                <span className="text-sm font-bold text-secondary-900 dark:text-white">
                  {balanceInfo.balance.toLocaleString()}
                </span>
              </div>

              <div className="border-t border-secondary-200 dark:border-secondary-700 pt-3 space-y-2">
                {/* Expirable tokens */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-amber-500" />
                    <span className="text-sm text-secondary-600 dark:text-secondary-400">
                      {t('tokens.expirable')}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                    {balanceInfo.expirable_balance.toLocaleString()}
                  </span>
                </div>

                {/* Permanent tokens */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <InfinityIcon className="h-3.5 w-3.5 text-green-500" />
                    <span className="text-sm text-secondary-600 dark:text-secondary-400">
                      {t('tokens.permanent')}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-green-600 dark:text-green-400">
                    {balanceInfo.permanent_balance.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Expiration warning */}
              {hasExpiringTokens && daysUntilExpiration !== null && (
                <div
                  className={`mt-3 p-2 rounded-lg text-xs ${
                    daysUntilExpiration <= 3
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                      : daysUntilExpiration <= 7
                        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                        : 'bg-secondary-50 dark:bg-secondary-700/50 text-secondary-600 dark:text-secondary-400'
                  }`}
                >
                  <div className="flex items-start gap-1.5">
                    {daysUntilExpiration <= 7 && (
                      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    )}
                    <span>
                      {balanceInfo.expiring_soon_amount.toLocaleString()} {t('tokens.expiringSoon')}{' '}
                      {daysUntilExpiration === 0
                        ? t('tokens.today')
                        : daysUntilExpiration === 1
                          ? t('tokens.tomorrow')
                          : t('tokens.inDays', { days: daysUntilExpiration })}{' '}
                      ({formatExpirationDate(balanceInfo.next_expiration!)})
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Desktop app promo - serverless web users only */}
          {isServerlessMode() && !isElectronMode() && (
            <div className="px-4 pb-1">
              <button
                onClick={() => {
                  setShowTooltip(false);
                  window.dispatchEvent(new CustomEvent('navigate-to-mode', { detail: 'download' }));
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-900/20 border border-emerald-500/20 hover:border-emerald-500/40 transition-colors group"
              >
                <Download className="h-3.5 w-3.5 text-emerald-400 group-hover:text-emerald-300 flex-shrink-0" />
                <span className="text-xs font-medium text-emerald-300 group-hover:text-emerald-200">
                  {t('tokens.desktopPromo', {
                    discount: sharedConstants.constants.misc.LOCAL_GPU_DISCOUNT_PERCENT,
                  })}
                </span>
              </button>
            </div>
          )}

          {/* Referral invite */}
          <div className="px-4 pb-1 border-t border-secondary-200 dark:border-secondary-700 pt-2">
            <ReferralShare compact />
          </div>

          {/* Footer with View History link */}
          <div className="px-4 py-2 bg-secondary-50 dark:bg-secondary-900/50 rounded-b-lg flex items-center justify-between">
            <p className="text-xs text-secondary-500 dark:text-secondary-400">
              {t('tokens.expirationHint')}
            </p>
            <button
              onClick={() => {
                setShowTooltip(false);
                window.dispatchEvent(new CustomEvent('open-billing-history'));
              }}
              className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-400 transition-colors"
            >
              <Receipt className="h-3 w-3" />
              {t('tokens.viewHistory', { defaultValue: 'History' })}
            </button>
          </div>
        </div>
      )}

      {/* Buy tokens button */}
      <button
        onClick={onBuyClick}
        className="flex items-center gap-1 px-2 py-1.5 min-h-[36px]
                   bg-gradient-to-r from-yellow-500 to-orange-500
                   hover:from-yellow-600 hover:to-orange-600
                   text-white text-sm font-medium rounded-lg
                   transition-all duration-200 shadow-sm hover:shadow-md"
        title={t('tokens.buyTokens')}
      >
        <Plus className="h-4 w-4" />
        <span className="hidden sm:inline">{t('tokens.buy')}</span>
      </button>
    </div>
  );
});
